import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  CheckCircle2, Clock, MapPin, CreditCard, Loader2, XCircle,
  Coffee, UtensilsCrossed, ChefHat, BellRing, FileText, Home,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import Layout from "@/components/layout/Layout";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { COMPANY_FISCAL_DATA, IGIC_RATE, VERIFACTU_TEXT } from "@/constants/fiscal";
import { LOCATIONS } from "@/types/orders";

// --- HELPERS ---
const SESSION_KEY = "session_mode_data";
const SESSION_EXPIRY_KEY = "session_mode_expiry";

const getSessionData = () => {
  try {
    const expiry = sessionStorage.getItem(SESSION_EXPIRY_KEY);
    if (!expiry || Date.now() >= parseInt(expiry, 10)) return null;
    const savedSession = sessionStorage.getItem(SESSION_KEY);
    if (!savedSession) return null;
    return JSON.parse(savedSession);
  } catch { return null; }
};

const buildSessionUrlWithFallback = (basePath: string): string => {
  try {
    const expiry = sessionStorage.getItem(SESSION_EXPIRY_KEY);
    if (expiry && Date.now() < parseInt(expiry, 10)) {
      const savedSession = sessionStorage.getItem(SESSION_KEY);
      if (savedSession) {
        const session = JSON.parse(savedSession);
        const params = new URLSearchParams();
        if (session.mode === "mesa" && session.tableNumber) {
          params.set("mesa", session.tableNumber.toString());
          if (session.locationCode) params.set("store", session.locationCode);
        } else if (session.mode === "pickup") {
          params.set("type", "pickup");
          if (session.locationCode) params.set("store", session.locationCode);
        }
        const queryString = params.toString();
        if (queryString) return `${basePath}?${queryString}`;
      }
    }
    return basePath;
  } catch { return basePath; }
};

interface OrderDetails {
  id: string;
  order_number: number;
  order_type: string;
  status: string;
  payment_method: string;
  payment_status: string;
  total: number;
  created_at: string;
  customer_name?: string;
  customer_phone?: string;
  customer_email?: string;
  pickup_date?: string;
  pickup_time?: string;
  pickup_location?: string;
  observations?: string;
  table_number?: number | null;
  stripe_session_id?: string | null;
  items?: Array<{
    product_name: string;
    quantity: number;
    unit_price: number;
    subtotal: number;
  }>;
}

const OrderSuccess = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  
  // ESTADOS
  const [isVerifying, setIsVerifying] = useState(true);
  const [orderNumber, setOrderNumber] = useState<number | null>(null);
  const [currentStatus, setCurrentStatus] = useState<string>("pendiente");
  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sessionData, setSessionData] = useState<any>(null);

  // --- ESTADO NUEVO: VIEW MODE ---
  const [viewMode, setViewMode] = useState<'loading' | 'static' | 'tracker'>('loading');

  useEffect(() => {
    setSessionData(getSessionData());
  }, []);

  const handleNavigateToHome = useCallback(() => {
    navigate(buildSessionUrlWithFallback("/"));
  }, [navigate]);

  // Public status polling (order_status_public is no longer directly readable from the client)
  useEffect(() => {
    if (!orderId) return;
    if (isVerifying) return;
    if (viewMode !== "tracker") return;

    let cancelled = false;

    const tick = async () => {
      try {
        const { data, error } = await supabase.functions.invoke("get-public-order-status", {
          body: { orderId },
        });

        if (cancelled) return;
        if (error || !data?.success) return;
        if (typeof data.status === "string") setCurrentStatus(data.status);
      } catch {
        // silent
      }
    };

    tick();
    const id = window.setInterval(tick, 3000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [orderId, isVerifying, viewMode]);

  // VERIFICACIÓN
  useEffect(() => {
    if (orderId) {
      verifyPaymentServerSide(orderId);
    } else {
      setError("No se encontró el ID del pedido");
      setIsVerifying(false);
      setViewMode('loading');
    }
  }, [orderId]);

  // --- LÓGICA CORE ---
  const determineViewMode = (order: OrderDetails) => {
    // Regla: Si pagó online y está confirmado y no tiene mesa -> PANTALLA ESTÁTICA
    const isPaidOnlineNoTable = 
        (order.payment_method === 'online' || order.payment_method === 'stripe') &&
        order.payment_status === 'pagado' &&
        !order.table_number;

    const hasDate = Boolean(order.pickup_date && order.pickup_date !== '');
    const isTypeEncargo = order.order_type === 'encargo';

    if (hasDate || isTypeEncargo || isPaidOnlineNoTable) {
        return 'static';
    } else {
        return 'tracker';
    }
  };

  const verifyPaymentServerSide = async (orderId: string) => {
    try {
      const { data: orderResult, error: fetchError } = await supabase.functions.invoke("get-order-details", { body: { orderId } });

      if (fetchError || !orderResult?.success) {
        setError(orderResult?.error || "Pedido no encontrado");
        setIsVerifying(false);
        return;
      }

      const order = orderResult.order;
      setCurrentStatus(order.status);
      setOrderDetails(order);

      // APLICAR MODO VISUAL
      const mode = determineViewMode(order);
      setViewMode(mode);

      if (order.payment_status === "pagado") {
        setOrderNumber(order.order_number);
        setIsVerifying(false);
        return;
      }

      if (order.status === "esperando_pago" && order.stripe_session_id) {
        const { data: verifyResult } = await supabase.functions.invoke("verify-payment", { body: { sessionId: order.stripe_session_id } });

        if (verifyResult?.success && verifyResult?.paymentStatus === "paid") {
          setOrderNumber(verifyResult.orderNumber);
          setIsVerifying(false);
          if (!order.table_number) setViewMode('static');
          return;
        } else {
          setError("El pago no se ha completado.");
          setIsVerifying(false);
          return;
        }
      }

      if (order.payment_status === "pago_en_tienda") {
        setOrderNumber(order.order_number);
        setIsVerifying(false);
        setViewMode('tracker');
        return;
      }
       setIsVerifying(false);
    } catch (err) {
      setError("No se pudo verificar el pedido.");
      setIsVerifying(false);
    }
  };

  const handleDownloadReceipt = useCallback(() => {
    if (!orderDetails) return;
    const printWindow = window.open("", "_blank", "width=302,height=800");
    if (!printWindow) return;
    const formatDate = (dateStr: string) => format(new Date(dateStr), "dd/MM/yyyy", { locale: es });
    const formatTime = (dateStr: string) => format(new Date(dateStr), "HH:mm:ss", { locale: es });
    const year = new Date(orderDetails.created_at).getFullYear();
    const invoiceNumber = `WEB-${year}-${orderDetails.order_number.toString().padStart(8, "0")}`;
    const totalWithTax = orderDetails.total;
    const baseImponible = totalWithTax / (1 + IGIC_RATE);
    const cuotaIGIC = totalWithTax - baseImponible;
    const itemsHtml = orderDetails.items?.map((item) => `<tr><td class="qty">${item.quantity}</td><td class="desc">${item.product_name}</td><td class="price">${item.unit_price.toFixed(2)}€</td><td class="subtotal">${item.subtotal.toFixed(2)}€</td></tr>`).join("") || '<tr><td colspan="4">Sin artículos</td></tr>';
    
    const html = `<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8"><title>Factura ${invoiceNumber}</title><style>@page { size: 80mm auto; margin: 0; } body { font-family: monospace; font-size: 11px; width: 80mm; padding: 3mm; color: #000; } .header { text-align: center; border-bottom: 2px dashed #000; padding-bottom: 8px; margin-bottom: 8px; } .items-table { width: 100%; border-collapse: collapse; margin: 8px 0; font-size: 10px; } .items-table th { text-align: left; border-bottom: 1px solid #000; } .items-table td.price, .items-table td.subtotal { text-align: right; } .payment-info { text-align: center; padding: 8px 0; margin-top: 6px; border: 2px solid #000; background: #e8f5e9; font-weight: bold; }</style></head><body><div class="header"><div style="font-size:18px;font-weight:bold;">PASTELERÍA REYES</div><div>${COMPANY_FISCAL_DATA.razonSocial}<br/>CIF: ${COMPANY_FISCAL_DATA.cif}</div></div><div style="text-align:center;margin:8px 0;"><strong>FACTURA SIMPLIFICADA</strong><br/>${invoiceNumber}<br/>${formatDate(orderDetails.created_at)} | ${formatTime(orderDetails.created_at)}</div><table class="items-table"><thead><tr><th>Ud.</th><th>Desc.</th><th>P.U</th><th>Total</th></tr></thead><tbody>${itemsHtml}</tbody></table><div style="border-top:1px solid #000;padding-top:8px;"><div style="display:flex;justify-content:space-between;"><span>Base:</span><span>${baseImponible.toFixed(2)}€</span></div><div style="display:flex;justify-content:space-between;"><span>IGIC (7%):</span><span>${cuotaIGIC.toFixed(2)}€</span></div><div style="display:flex;justify-content:space-between;font-weight:bold;font-size:14px;"><span>TOTAL:</span><span>${totalWithTax.toFixed(2)}€</span></div></div><div class="payment-info">✓ PAGADO ONLINE</div><div style="text-align:center;font-size:9px;margin-top:8px;">${VERIFACTU_TEXT}</div></body></html>`;
    printWindow.document.write(html);
    printWindow.document.close();
  }, [orderDetails]);

  const formatPickupDate = (dateStr: string) => { try { return format(new Date(dateStr), "EEEE, d 'de' MMMM 'de' yyyy", { locale: es }); } catch { return dateStr; } };
  const getLocationName = (locationCode?: string) => { if (!locationCode) return null; const location = LOCATIONS.find(l => l.id === locationCode || l.code === locationCode); return location?.name || locationCode; };

  if (isVerifying) return <Layout><div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin" /><span className="ml-2">Verificando pago...</span></div></Layout>;
  if (error) return <Layout><div className="min-h-screen flex flex-col items-center justify-center text-center p-4"><XCircle className="h-16 w-16 text-destructive mb-4" /><p className="text-lg mb-4">{error}</p><Button onClick={() => navigate("/")}>Volver</Button></div></Layout>;

  // --- VISTA 1: ESTÁTICA ---
  if (viewMode === 'static' && orderDetails) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex flex-col items-center justify-center p-6 text-center">
        <CheckCircle2 className="h-20 w-20 text-green-500 mb-6" />
        <h1 className="text-3xl font-bold text-foreground mb-2">¡Gracias por tu encargo!</h1>
        <p className="text-lg text-muted-foreground mb-4">Pedido #{orderNumber} confirmado.</p>
        {orderDetails.pickup_date && (
          <div className="bg-white rounded-lg shadow-md p-4 mb-6">
            <p className="font-semibold">{formatPickupDate(orderDetails.pickup_date)}</p>
            {orderDetails.pickup_time && <p className="text-muted-foreground">{orderDetails.pickup_time}</p>}
          </div>
        )}
        <Button onClick={() => window.location.href = '/'} size="lg">Volver al inicio</Button>
      </div>
    );
  }

  // --- VISTA 2: TRACKER ---
  const isCompleted = currentStatus === "completado";
  const isOnlinePayment = orderDetails?.payment_method === "online" || orderDetails?.payment_method === "stripe";

  return (
    <Layout>
      <div className="min-h-screen flex flex-col items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <div className="mb-4">
              <p className="text-2xl font-bold">{isCompleted ? "¡Listo!" : "Preparando..."}</p>
               {orderNumber && <p className="text-muted-foreground">Pedido #{orderNumber}</p>}
            </div>
            <p className="text-lg mb-6">{isCompleted ? "Listo para recoger" : "En cocina"}</p>
            {isOnlinePayment && <Button variant="outline" onClick={handleDownloadReceipt} className="mb-2 w-full"><FileText className="mr-2 h-4 w-4" /> Descargar Factura</Button>}
            <Button onClick={handleNavigateToHome} className="w-full">Volver</Button>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default OrderSuccess;
